import { ServiceMark, StatusPill } from "./primitives";
import { useSnapshot } from "@/lib/snapshot-context";
import type { Incident } from "@/lib/types";

interface Props {
  incident: Incident;
  compact?: boolean;
  onPick?: () => void;
}

export function IncidentRow({ incident, compact, onPick }: Props) {
  const { data, fmt } = useSnapshot();
  const svc = data.services.find((s) => s.id === incident.serviceId);
  if (!svc) return null;
  const sevColor =
    incident.severity === "major" ? "var(--destructive)" : "var(--warning)";
  return (
    <div
      role="button"
      onClick={onPick}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "12px 16px",
        borderTop: "1px solid var(--border)",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          width: 3,
          alignSelf: "stretch",
          background: sevColor,
          borderRadius: 2,
          marginTop: 2,
          marginBottom: 2,
        }}
      />
      <ServiceMark service={svc} size={22} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
        >
          <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>
            {incident.title}
          </div>
          {!compact && (
            <StatusPill
              state={
                incident.status === "resolved"
                  ? "operational"
                  : incident.severity === "major"
                    ? "major-outage"
                    : "degraded"
              }
            />
          )}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--muted-foreground)",
            marginTop: 3,
            display: "flex",
            gap: 8,
          }}
        >
          <span>{svc.name}</span>
          {incident.region && (
            <>
              <span>·</span>
              <span
                style={{
                  fontFamily: "var(--font-mono, ui-monospace, monospace)",
                  color: "var(--foreground)",
                }}
              >
                {incident.region}
              </span>
            </>
          )}
          <span>·</span>
          <span>
            {incident.status === "resolved" && incident.resolvedAt
              ? `resolved ${fmt.rel(incident.resolvedAt)} · lasted ${fmt.duration(incident.startedAt, incident.resolvedAt)}`
              : `started ${fmt.rel(incident.startedAt)} · ${fmt.duration(incident.startedAt)} ongoing`}
          </span>
        </div>
      </div>
    </div>
  );
}
